import { File, FileAudio, FileVideo, Image, X } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { AssetFile } from "@/features/asset";
import { AudioPlayer } from "./AudioPlayer";

// Format file size for display
const formatFileSize = (bytes?: number): string => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Get icon component based on MIME type
const getAssetIcon = (mimeType: string, size = 48): React.ReactNode => {
  if (mimeType.startsWith("image/")) {
    return <Image size={size} className="text-slate-400" />;
  }
  if (mimeType.startsWith("audio/")) {
    return <FileAudio size={size} className="text-slate-400" />;
  }
  if (mimeType.startsWith("video/")) {
    return <FileVideo size={size} className="text-slate-400" />;
  }
  return <File size={size} className="text-slate-400" />;
};

// Modal component for viewing full media
const MediaModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  assetUrl: string;
  assetName: string;
  mimeType: string;
}> = ({ isOpen, onClose, assetUrl, assetName, mimeType }) => {
  const isVideo = mimeType.startsWith("video/");

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-100 flex items-center justify-center bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative max-w-[90vw] max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute -top-10 right-0 p-2 text-slate-300 hover:text-white transition-colors"
          title="Close"
        >
          <X size={24} />
        </button>

        {/* Media content */}
        {isVideo ? (
          <video src={assetUrl} controls autoPlay className="max-w-full max-h-[85vh] rounded-lg">
            <track kind="captions" />
          </video>
        ) : (
          <img
            src={assetUrl}
            alt={assetName}
            className="max-w-full max-h-[85vh] object-contain rounded-lg"
          />
        )}

        {/* Filename */}
        <p className="text-sm text-slate-300 mt-3 text-center truncate">{assetName}</p>
      </div>
    </div>
  );
};

// Video thumbnail component using video element directly
const VideoThumbnail: React.FC<{
  src: string;
  onClick: () => void;
}> = ({ src, onClick }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className="relative w-full h-full cursor-pointer hover:opacity-80 transition-opacity"
    >
      <video src={src} className="w-full h-full object-cover" preload="metadata" muted playsInline>
        <track kind="captions" />
      </video>
      {/* Play icon overlay */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-12 h-12 rounded-full bg-black/50 flex items-center justify-center">
          <div className="w-0 h-0 border-t-[8px] border-t-transparent border-l-[14px] border-l-white border-b-[8px] border-b-transparent ml-1" />
        </div>
      </div>
    </button>
  );
};

interface AssetCardProps {
  asset: AssetFile;
  assetUrl: string | undefined;
  onDelete: (assetId: string) => void;
  onRename: (assetId: string, newName: string) => void;
  selectionMode?: boolean;
  selectable?: boolean;
  onSelect?: (assetId: string) => void;
}

export const AssetCard: React.FC<AssetCardProps> = ({
  asset,
  assetUrl,
  onDelete,
  onRename,
  selectionMode = false,
  selectable = true,
  onSelect,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(asset.name);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const isMissing = !assetUrl;
  const isImage = asset.mimeType.startsWith("image/");
  const isVideo = asset.mimeType.startsWith("video/");
  const isAudio = asset.mimeType.startsWith("audio/");

  // Sync editName with asset.name when not editing
  useEffect(() => {
    if (!isEditing) {
      setEditName(asset.name);
    }
  }, [asset.name, isEditing]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      // Select filename without extension
      const extIndex = asset.name.lastIndexOf(".");
      if (extIndex > 0) {
        inputRef.current.setSelectionRange(0, extIndex);
      } else {
        inputRef.current.select();
      }
    }
  }, [isEditing, asset.name]);

  const handleSaveRename = () => {
    const trimmedName = editName.trim();
    if (trimmedName && trimmedName !== asset.name) {
      onRename(asset.id, trimmedName);
    } else {
      setEditName(asset.name);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSaveRename();
    } else if (e.key === "Escape") {
      setEditName(asset.name);
      setIsEditing(false);
    }
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(asset.id);
    setShowDeleteConfirm(false);
  };

  const handleCardClick = () => {
    if (selectionMode && selectable && onSelect) {
      onSelect(asset.id);
    }
  };

  const handleThumbnailClick = useCallback(() => {
    if (!selectionMode && assetUrl && (isImage || isVideo)) {
      setIsModalOpen(true);
    }
  }, [selectionMode, assetUrl, isImage, isVideo]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  return (
    <>
      <div
        className={`relative bg-slate-800 rounded-xl border p-4 transition-all ${
          selectionMode
            ? selectable
              ? "border-slate-700 hover:border-indigo-500 cursor-pointer"
              : "border-slate-700 opacity-40 cursor-not-allowed"
            : "border-slate-700 hover:border-slate-600"
        }`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={handleCardClick}
      >
        {/* Delete button - shown on hover, not in selection mode */}
        {isHovered && !showDeleteConfirm && !selectionMode && (
          <button
            onClick={handleDeleteClick}
            className="absolute top-3 right-3 p-1.5 bg-slate-900/80 hover:bg-red-600 rounded-md transition-colors z-10"
            title="Delete Asset"
          >
            <X size={16} className="text-slate-300" />
          </button>
        )}

        {/* Delete confirmation */}
        {showDeleteConfirm && !selectionMode && (
          <div className="absolute inset-0 bg-slate-900/95 rounded-xl flex flex-col items-center justify-center z-20 p-4">
            <p className="text-sm text-slate-300 mb-3 text-center">Confirm delete?</p>
            <div className="flex gap-3">
              <button
                onClick={handleConfirmDelete}
                className="px-3 py-1.5 bg-red-600 hover:bg-red-500 text-white text-sm rounded"
              >
                Delete
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDeleteConfirm(false);
                }}
                className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm rounded"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Thumbnail area - larger with 4:3 aspect ratio */}
        <div className="w-full aspect-[4/3] bg-slate-900 rounded-lg flex items-center justify-center mb-3 overflow-hidden">
          {isMissing ? (
            <div className="flex flex-col items-center gap-2 text-slate-500">
              <File size={48} />
              <span className="text-xs">File Missing</span>
            </div>
          ) : isImage && assetUrl ? (
            <button
              type="button"
              onClick={handleThumbnailClick}
              className="w-full h-full cursor-pointer hover:opacity-80 transition-opacity"
            >
              <img src={assetUrl} alt={asset.name} className="w-full h-full object-cover" />
            </button>
          ) : isVideo && assetUrl ? (
            <VideoThumbnail src={assetUrl} onClick={handleThumbnailClick} />
          ) : isAudio && assetUrl ? (
            <AudioPlayer src={assetUrl} />
          ) : (
            getAssetIcon(asset.mimeType, 48)
          )}
        </div>

        {/* Filename - editable only when not in selection mode */}
        {isEditing && !selectionMode ? (
          <input
            ref={inputRef}
            type="text"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onBlur={handleSaveRename}
            onKeyDown={handleKeyDown}
            className="w-full bg-slate-700 text-slate-200 md:text-sm px-2 py-1 rounded border border-indigo-500 focus:outline-none"
          />
        ) : (
          <p
            onClick={(e) => {
              if (!selectionMode) {
                e.stopPropagation();
                setIsEditing(true);
              }
            }}
            className={`text-sm text-slate-200 truncate ${
              selectionMode ? "" : "cursor-pointer hover:text-indigo-400"
            }`}
            title={asset.name}
          >
            {asset.name}
          </p>
        )}

        {/* File size */}
        <p className="text-xs text-slate-500 mt-1">{formatFileSize(asset.size)}</p>
      </div>

      {/* Media modal */}
      {assetUrl && (isImage || isVideo) && (
        <MediaModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          assetUrl={assetUrl}
          assetName={asset.name}
          mimeType={asset.mimeType}
        />
      )}
    </>
  );
};
